import type { YuzuBlock, YuzuTemplateSchema } from '@giftie/yuzu-integration'

export type YuzuTextResolution = {
  fontSizePt: number
  maxFontSizePt: number
  styles: Partial<
    Pick<YuzuBlock['styles'], 'left' | 'top' | 'width' | 'height'>
  >
}

function positiveNumber(value: unknown): number | null {
  const parsed =
    typeof value === 'number' ? value : Number.parseFloat(String(value))
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

/**
 * Fresh templates shrink from their configured font size. Restored sessions
 * reuse the saved ceiling, bounded by the original template when available.
 * Yuzu-created sessions can still contain the editor's default maximum of 100.
 */
export function getYuzuTextMaxFontSizePt(
  block: YuzuBlock,
  measuredFontSizePt: number,
  restoredFromSession: boolean,
  templateFontSizePt?: number,
) {
  const measuredSize = positiveNumber(measuredFontSizePt) ?? 1
  if (!restoredFromSession) return measuredSize
  return Math.min(
    positiveNumber(block.attributes.maxFontSize) ?? measuredSize,
    positiveNumber(templateFontSizePt) ?? Infinity,
  )
}

function formatFontSizePt(value: number) {
  return `${Number(value.toFixed(3))}pt`
}

function freezeBlock(
  block: YuzuBlock,
  resolutions: Readonly<Record<string, YuzuTextResolution>>,
): YuzuBlock {
  const children = block.children.map((child) =>
    typeof child === 'object' ? freezeBlock(child, resolutions) : child,
  )
  const resolution = block.type === 'text' ? resolutions[block.id] : undefined

  if (!resolution) {
    return { ...block, children }
  }

  return {
    ...block,
    attributes: {
      ...block.attributes,
      autoResize: false,
      maxFontSize: positiveNumber(resolution.maxFontSizePt) ?? 1,
    },
    styles: {
      ...block.styles,
      ...resolution.styles,
      fontSize: formatFontSizePt(resolution.fontSizePt),
    },
    children,
  }
}

/**
 * Creates the immutable session/render snapshot. Resolved text blocks use the
 * exact storefront font size and bounds so rendering can consume the stored
 * schema without recomputing its layout.
 */
export function freezeYuzuSessionText(
  schema: YuzuTemplateSchema,
  resolutions: Readonly<Record<string, YuzuTextResolution>>,
): YuzuTemplateSchema {
  return {
    ...schema,
    pages: schema.pages.map((page) => ({
      ...page,
      blocks: page.blocks.map((block) => freezeBlock(block, resolutions)),
    })),
  }
}
